import { useHandler } from "@redotech/react-util/hook";
import type { CarrierAndService } from "@redotech/redo-fulfillment-carrier-common/carriers-and-services";
import {
  FulfillmentGroupStatus,
  isEditingLockedStatus,
} from "@redotech/redo-fulfillment-common/fulfillment-group-status";
import {
  FulfillmentGroupActions,
  WeightFormat,
} from "@redotech/redo-fulfillment-outbound-labels-common/outbound-label-enums";
import {
  Weight,
  WeightUnit,
} from "@redotech/redo-fulfillment-outbound-labels-common/util";
import {
  OutboundParcel,
  Parcel,
  ParcelType,
} from "@redotech/redo-fulfillment-parcel-common/parcel";
import CopyIcon from "@redotech/redo-icons/copy-01.svg";
import {
  TotalWeightWidget,
  WeightChangeSource,
} from "@redotech/redo-merchant-app-common/components/weight-widget/total-weight-widget";
import { toast } from "@redotech/redo-web/alert";
import { RedoButton } from "@redotech/redo-web/arbiter-components/buttons/redo-button";
import { TeamContext } from "@redotech/redo-web/context/team-context";
import { Flex } from "@redotech/redo-web/flex";
import { Text } from "@redotech/redo-web/text";
import { Tooltip } from "@redotech/redo-web/tooltip/tooltip";
import { memo, useContext, useMemo } from "react";
import { LearningIcon } from "../learning-icon";
import { ParcelSelectDropdown } from "./parcel-select-dropdown";
import { ParcelSizeWidget } from "./parcel-size-widget";

export const ParcelInput = memo(function SingleParcelDetails({
  displayedButtonAction,
  everythingDisabled,
  mixedParcels,
  onParcelChange,
  parcelValues,
  selectedParcel,
  selectedRate,
  selectedStatus,
  mixedWeight,
  usedPackageLearning,
  usedWeightLearning,
  disableAutoReadFromScale = false,
  onAddPackage,
  trackingNumber,
}: {
  displayedButtonAction: FulfillmentGroupActions;
  everythingDisabled: boolean;
  mixedParcels: boolean;
  onParcelChange: (
    parcel: Partial<OutboundParcel>,
    source?: WeightChangeSource,
  ) => void;
  parcelValues: readonly Parcel[];
  mixedWeight: boolean;
  selectedParcel: OutboundParcel | null;
  selectedRate?: CarrierAndService | null;
  selectedStatus: FulfillmentGroupStatus | null | undefined;
  usedPackageLearning: boolean;
  usedWeightLearning: boolean;
  disableAutoReadFromScale?: boolean;
  onAddPackage?: () => void;
  trackingNumber?: string;
}) {
  const team = useContext(TeamContext);

  const totalWeight = useMemo(() => {
    return mixedWeight
      ? null
      : (selectedParcel?.parcelAndContentWeight ?? {
          value: 0,
          unit: WeightUnit.GRAM,
        });
  }, [selectedParcel, mixedWeight]);

  const weightFormat =
    team?.settings?.outboundLabels?.weightFormat ?? WeightFormat.IMPERIAL;

  const onWeightChange = useHandler(
    (weight: Weight, source?: WeightChangeSource) => {
      onParcelChange({ parcelAndContentWeight: weight }, source);
    },
  );

  const onWeightReset = useHandler(() => {
    onParcelChange({ parcelAndContentWeight: null });
  });

  const onSelectedParcelChange = useHandler((parcel: OutboundParcel) => {
    const { parcelAndContentWeight, ...rest } = parcel;
    onParcelChange({ ...rest });
  });

  const copyTrackingNumber = useHandler(async () => {
    if (!trackingNumber) {
      return;
    }

    if (!navigator.clipboard?.writeText) {
      toast("Unable to copy tracking number", { variant: "error" });
      return;
    }

    try {
      await navigator.clipboard.writeText(trackingNumber);
      toast("Tracking number copied", { variant: "success" });
    } catch {
      toast("Unable to copy tracking number", { variant: "error" });
    }
  });

  const isDisabled =
    everythingDisabled ||
    isEditingLockedStatus(selectedStatus) ||
    displayedButtonAction === FulfillmentGroupActions.MARK_AS_FULFILLED;

  const dimensionText =
    selectedParcel?.parcelType === ParcelType.CARRIER
      ? selectedParcel.dimensions.length > 1
        ? "Dimensions (multiple options)"
        : "Dimensions"
      : null;

  return (
    <Flex dir="column" gap="2xl">
      {trackingNumber && (
        <Flex align="center" gap="xs">
          <Text color="tertiary" fontSize="xs" fontWeight="regular">
            Tracking number:
          </Text>
          <Tooltip title="Click to copy">
            <Text
              cursor="pointer"
              fontSize="xs"
              onClick={copyTrackingNumber}
              textColor="tertiary"
              textDecoration="underline"
            >
              {trackingNumber}
            </Text>
          </Tooltip>
          <Tooltip title="Click to copy">
            <RedoButton
              hierarchy="tertiary"
              IconLeading={CopyIcon}
              onClick={copyTrackingNumber}
              size="xs"
            />
          </Tooltip>
        </Flex>
      )}
      <TotalWeightWidget
        disableAutoReadFromScale={disableAutoReadFromScale}
        disabled={isDisabled}
        hideScale={false}
        onWeightChange={onWeightChange}
        onWeightReset={onWeightReset}
        titleIcon={usedWeightLearning ? <LearningIcon /> : undefined}
        weight={totalWeight}
        weightFormat={weightFormat}
      />
      <ParcelSelectDropdown
        disabled={isDisabled}
        label="Package"
        mixedParcels={mixedParcels}
        onAddPackage={onAddPackage}
        onParcelChange={onSelectedParcelChange}
        parcelValues={parcelValues}
        selectedParcel={selectedParcel}
        usedPackageLearning={usedPackageLearning}
      />
      {selectedParcel?.parcelType === ParcelType.CARRIER ? (
        <Flex dir="column" gap="xs">
          <Text fontSize="xs">{dimensionText}</Text>
          {selectedParcel.dimensions.map((dimension) => (
            <Text fontSize="xs" key={dimension}>
              {dimension}
            </Text>
          ))}
        </Flex>
      ) : null}
      {selectedParcel?.parcelType === ParcelType.CUSTOM ? (
        <ParcelSizeWidget
          disabled={isDisabled}
          selectedParcel={mixedParcels ? null : selectedParcel}
          selectedRate={selectedRate}
          updateParcel={onParcelChange}
        />
      ) : null}
    </Flex>
  );
});
